// src/lib/metrics.ts
// Application-level performance metrics that feed into Sentry spans.

import * as Sentry from '@sentry/react';
import logger from './logger';

// ---------------------------------------------------------------------------
// Counters and histograms (in-memory, reported via Sentry tags/context)
// ---------------------------------------------------------------------------

interface MetricBucket {
  count: number;
  totalMs: number;
  maxMs: number;
}

const apiBuckets = new Map<string, MetricBucket>();

function getOrCreate(endpoint: string): MetricBucket {
  let bucket = apiBuckets.get(endpoint);
  if (!bucket) {
    bucket = { count: 0, totalMs: 0, maxMs: 0 };
    apiBuckets.set(endpoint, bucket);
  }
  return bucket;
}

/**
 * Record an API call duration for a given endpoint.
 * Data is available to Sentry via periodic flush and to
 * any local monitoring dashboards.
 */
export function recordApiCall(endpoint: string, durationMs: number, ok: boolean): void {
  const bucket = getOrCreate(endpoint);
  bucket.count += 1;
  bucket.totalMs += durationMs;
  bucket.maxMs = Math.max(bucket.maxMs, durationMs);

  Sentry.setMeasurement(`api.${endpoint}.duration`, durationMs, 'millisecond');

  if (!ok) {
    Sentry.setMeasurement(`api.${endpoint}.error`, 1, 'none');
  }
}

/**
 * Snapshot all collected API metrics (useful for debugging / health dashboards).
 */
export function getApiMetricsSnapshot(): Record<string, MetricBucket> {
  const out: Record<string, MetricBucket> = {};
  apiBuckets.forEach((v, k) => {
    out[k] = { ...v };
  });
  return out;
}

// ---------------------------------------------------------------------------
// Navigation timing (reported once after page load)
// ---------------------------------------------------------------------------

function reportNavigationTiming(): void {
  const perf = performance.getEntriesByType('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined;
  if (!perf) return;

  const metrics = {
    dns_ms: Math.round(perf.domainLookupEnd - perf.domainLookupStart),
    tcp_ms: Math.round(perf.connectEnd - perf.connectStart),
    ttfb_ms: Math.round(perf.responseStart - perf.requestStart),
    dom_interactive_ms: Math.round(perf.domInteractive - perf.startTime),
    dom_complete_ms: Math.round(perf.domComplete - perf.startTime),
    load_ms: Math.round(perf.loadEventEnd - perf.startTime),
  };

  Sentry.setContext('navigation_timing', metrics);

  logger.info('Navigation timing', metrics);
}

// ---------------------------------------------------------------------------
// Long-task observer (supplements Sentry's enableLongTask)
// ---------------------------------------------------------------------------

let longTaskCount = 0;

function observeLongTasks(): void {
  if (typeof PerformanceObserver === 'undefined') return;

  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        longTaskCount += 1;
        if (entry.duration > 100) {
          logger.warn('Long task detected', {
            duration_ms: Math.round(entry.duration),
            count: longTaskCount,
          });
        }
      }
    });
    observer.observe({ type: 'longtask', buffered: true });
  } catch {
    // PerformanceObserver for longtask not supported
  }
}

// ---------------------------------------------------------------------------
// Initialisation
// ---------------------------------------------------------------------------

export function initMetrics(): void {
  if (typeof window === 'undefined') return;

  // Report navigation timing after the page fully loads
  if (document.readyState === 'complete') {
    reportNavigationTiming();
  } else {
    window.addEventListener('load', reportNavigationTiming, { once: true });
  }

  observeLongTasks();

  logger.info('Performance metrics instrumentation initialized');
}
